import { Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import type { ReaderFont, ReaderFontChoice } from './reader-fonts'

type Props = {
  id?: string
  value: ReaderFont
  options: readonly ReaderFontChoice[]
  onChange: (value: ReaderFont) => void
}

const SAMPLE_TEXT = '永あア Aa'

/**
 * フォント選択。選んでも閉じないので、プレビューを見ながら続けて試せる。
 * 左右ボタン / ↑↓キーで順番に切り替えることもできる。
 */
export const ReaderFontPicker = ({ id, value, options, onChange }: Props) => {
  const [open, setOpen] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const currentIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  )
  const current = options.at(currentIndex)

  const step = (delta: number) => {
    if (options.length === 0) return
    const next = options.at((currentIndex + delta + options.length) % options.length)
    if (next) onChange(next.value)
  }

  // 開いたとき・切り替えたときに選択中の項目をリスト内で見える位置へ
  useEffect(() => {
    if (!open) return
    const frame = requestAnimationFrame(() => {
      listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(frame)
  }, [open, value])

  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      step(event.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      setOpen(false)
    }
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="size-8 shrink-0"
        aria-label="前のフォント"
        onClick={() => step(-1)}
      >
        <ChevronLeft />
      </Button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            role="combobox"
            aria-expanded={open}
            className="flex h-9 min-w-0 flex-1 items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs transition-colors hover:bg-accent/50 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none dark:bg-input/30"
            style={{ fontFamily: current?.family ?? undefined }}
          >
            <span className="truncate">{current?.label}</span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="center"
          className="z-10001 flex max-h-[min(20rem,var(--radix-popover-content-available-height))] w-(--radix-popover-trigger-width) min-w-60 flex-col p-1"
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            listRef.current?.focus()
          }}
        >
          <p className="px-2 pt-1 pb-1.5 text-[11px] text-muted-foreground">選ぶとすぐプレビューに反映されます</p>
          <div
            ref={listRef}
            role="listbox"
            aria-label="フォント"
            tabIndex={-1}
            onKeyDown={handleListKeyDown}
            className="min-h-0 flex-1 overflow-y-auto pr-1.5 outline-none"
          >
            {options.map((option) => {
              const selected = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  tabIndex={-1}
                  onClick={() => onChange(option.value)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left transition-colors hover:bg-accent/60',
                    selected && 'bg-accent text-accent-foreground',
                  )}
                  style={{ fontFamily: option.family ?? undefined }}
                >
                  <Check className={cn('size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0 flex-1 truncate text-sm">{option.label}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{SAMPLE_TEXT}</span>
                </button>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>

      <Button variant="ghost" size="icon" className="size-8 shrink-0" aria-label="次のフォント" onClick={() => step(1)}>
        <ChevronRight />
      </Button>
    </div>
  )
}
